import type { KeyboardEvent, ReactNode } from "react";
import Icon from "../Icon/Icon";
import "./WorkspaceCard.css";

export interface WorkspaceCardProps {
  name: string;
  /** picture URL; without it the first letter of the name shows */
  image?: string;
  /** next to the name, e.g. <Badge>Default</Badge> */
  badge?: ReactNode;
  /** grey lines under the name, e.g. members and last activity */
  lines?: ReactNode[];
  onClick?: () => void;
  className?: string;
}

/**
 * A workspace as a clickable card.
 * Example: <WorkspaceCard name="Landlord" lines={["1 member"]} onClick={open} />
 */
const WorkspaceCard = ({
  name,
  image,
  badge,
  lines = [],
  onClick,
  className = "",
}: WorkspaceCardProps) => {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`ds-workspace-card ${className}`.trim()}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <span
        className={`ds-workspace-card__picture ${image ? "has-image" : ""}`}
        aria-hidden="true"
      >
        {image ? <img src={image} alt="" /> : name?.substring(0, 1)}
      </span>
      <span className="ds-workspace-card__body">
        <span className="ds-workspace-card__title">
          <h5 className="ds-workspace-card__name">{name}</h5>
          {badge}
        </span>
        {lines.filter(Boolean).map((line, i) => (
          <p key={i} className="ds-workspace-card__line">
            {line}
          </p>
        ))}
      </span>
      <Icon
        name="chevronRight"
        size={18}
        className="ds-workspace-card__arrow"
      />
    </div>
  );
};

export default WorkspaceCard;
